import { SEMANTIC_SUCCESS } from '@virtuetasks/types';
/**
 * Named reports — spec §8.1 `/reports/:name`, §5-F13 ("every report exports to
 * CSV").
 *
 * Three reports exist server-side: throughput, cycle-time and time. An unknown
 * name gets a list of the real ones rather than a blank screen.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Download } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ChartCard, SimpleBarChart, SimpleLineChart } from './charts';
import { reportExportUrl, useCycleTime, useThroughput, useTimeReport } from './api';

const REPORTS = [
  { name: 'throughput', label: 'Throughput', description: 'Tasks completed per week, by project' },
  { name: 'cycle-time', label: 'Cycle time', description: 'First in-progress → done, median and p85' },
  { name: 'time', label: 'Time logged', description: 'Hours per person, billable split' },
] as const;

function ThroughputReport() {
  const [groupBy, setGroupBy] = useState<'project' | 'user' | 'week'>('project');
  const { data = [], isLoading } = useThroughput({ groupBy });

  return (
    <ChartCard
      title="Throughput"
      subtitle={`Tasks completed, by ${groupBy}`}
      testId="report-body"
      actions={
        <select
          aria-label="Group by"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
          className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-1.5 text-[12px]"
        >
          <option value="project">By project</option>
          <option value="user">By person</option>
          <option value="week">By week</option>
        </select>
      }
    >
      {isLoading ? (
        <Skeleton className="h-64" />
      ) : data.length === 0 ? (
        <p className="py-8 text-center text-[12px] vt-muted">Nothing completed in this period.</p>
      ) : (
        <SimpleBarChart data={data as never} xKey="label" yKey="completed" height={280} />
      )}
    </ChartCard>
  );
}

function CycleTimeReport() {
  const { data = [], isLoading } = useCycleTime();

  return (
    <ChartCard
      title="Cycle time"
      subtitle="Working days from first in-progress to done"
      testId="report-body"
    >
      {isLoading ? (
        <Skeleton className="h-64" />
      ) : data.length === 0 ? (
        <p className="py-8 text-center text-[12px] vt-muted">Not enough completed work yet.</p>
      ) : (
        <SimpleLineChart
          data={data.map((r) => ({ ...r, week: (r.week ?? '').slice(5) })) as never}
          xKey="week"
          height={280}
          series={[{ key: 'medianDays', label: 'Median days', colour: SEMANTIC_SUCCESS }]}
        />
      )}
    </ChartCard>
  );
}

function TimeReport() {
  const { data = [], isLoading } = useTimeReport();
  const rows = data.map((r) => ({ ...r, hours: Math.round((r.minutes / 60) * 10) / 10 }));

  return (
    <ChartCard title="Time logged" subtitle="Hours per person" testId="report-body">
      {isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-[12px] vt-muted">No time logged in this period.</p>
      ) : (
        <SimpleBarChart data={rows as never} xKey="label" yKey="hours" height={280} />
      )}
    </ChartCard>
  );
}

export function ReportPage() {
  const { name } = useParams();
  const report = REPORTS.find((r) => r.name === name);

  if (!report) {
    return (
      <>
        <PageHeader title="Reports" />
        <div className="p-4">
          <EmptyState
            icon={<Download className="size-6" />}
            title={name ? `No report called “${name}”` : 'Pick a report'}
            description="These are the reports available."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                {REPORTS.map((r) => (
                  <Link key={r.name} to={`/reports/${r.name}`}>
                    <Button variant="subtle" size="sm">
                      {r.label}
                    </Button>
                  </Link>
                ))}
              </div>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={report.label}
        subtitle={report.description}
        actions={
          <a href={reportExportUrl(report.name)} download>
            <Button variant="ghost" size="sm">
              <Download />
              Export CSV
            </Button>
          </a>
        }
        toolbar={
          <div className="flex gap-1">
            {REPORTS.map((r) => (
              <Link key={r.name} to={`/reports/${r.name}`}>
                <button
                  type="button"
                  aria-current={r.name === report.name}
                  className={`rounded-[6px] px-2 py-1 text-[12px] transition-colors duration-[120ms] ${
                    r.name === report.name
                      ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                      : 'vt-muted hover:bg-[var(--surface-2)]'
                  }`}
                >
                  {r.label}
                </button>
              </Link>
            ))}
          </div>
        }
      />
      <div className="p-4">
        {report.name === 'throughput' && <ThroughputReport />}
        {report.name === 'cycle-time' && <CycleTimeReport />}
        {report.name === 'time' && <TimeReport />}
      </div>
    </>
  );
}
